// ============================================================================
// POST CHARGE — a Folio Charge Voucher posted at the desk (PMS slices 5, 7c)
// ============================================================================
// Charge types the master allows by hand (Room and Meal plan are night audit's).
// Dated on the business date; back-dating only within the stay. Tax is computed
// by the server for the payer the settlement mode names and shown before posting
// — the preview is the same calculation the posting runs. A refusal (room
// charging off, credit limit, folio closed) is shown with what to do, not as an
// error. One client reference per dialog, so a double click posts once.
//
// Every desk charge gets a numbered voucher (slice 7c). The charge type's nature
// decides what it must carry:
//   Service       — taxed as mapped;
//   Recovery      — damage / loss: the reason (and a photo if there is one), its own permission;
//   Disbursement  — paid out for the guest at cost: the supplier bill and its copy,
//                   who paid it (desk cash or Accounts), never taxed, its own permission.
// After checkout, inside the property's late-charge window, a late charge reopens
// the folio (post-late-charge) and is billed on a supplementary invoice.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { FileUploadField, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { chargeTypeService } from 'api/services/chargeTypeService';
import { folioService } from 'api/services/folioService';
import { formatCurrency } from 'utils/currencyFormat';
import { reserveTab, showPdf } from 'utils/reportFile';
import { CHARGE_NATURE_OPTIONS, type ChargeType } from 'api/types/chargeType.types';
import type { StoredFileInfo } from 'api/types/fileStorage.types';
import {
  INHOUSE_ACTIONS,
  PAID_OUT_OPTIONS,
  type FolioChargePreview,
  type FolioDetail,
  type PostFolioChargeRequest,
} from 'api/types/folio.types';

interface Props {
  isOpen: boolean;
  folio: FolioDetail | null;
  /** The user's pms-inhouse actions: recoveries, disbursements and late charges need their own. */
  allowedActions?: string[];
  onClose: () => void;
  onPosted: () => void;
}

const newRef = (): string =>
  (globalThis.crypto && 'randomUUID' in globalThis.crypto)
    ? globalThis.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });

const isoDate = (v?: string | null) => (v ? v.slice(0, 10) : '');
const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const natureOf = (t?: ChargeType | null) => t?.chargeNature ?? 'SERVICE';

const PostChargeModal: React.FC<Props> = ({ isOpen, folio, allowedActions, onClose, onPosted }) => {
  // No list given (older callers): the server still checks every action.
  const can = useCallback((a: string) => !allowedActions || allowedActions.includes(a), [allowedActions]);
  const [types, setTypes] = useState<ChargeType[]>([]);
  const [chargeCode, setChargeCode] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');
  const [chargeDate, setChargeDate] = useState('');
  /** The tick; null until touched = the property's "Prices include tax" setting (the preview says which). */
  const [includesTax, setIncludesTax] = useState<boolean | null>(null);
  const [reference, setReference] = useState('');
  const [referenceDate, setReferenceDate] = useState('');
  const [attachment, setAttachment] = useState<StoredFileInfo | null>(null);
  const [reason, setReason] = useState('');
  const [paidOut, setPaidOut] = useState('');
  const [printAfter, setPrintAfter] = useState(true);
  const [preview, setPreview] = useState<FolioChargePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const clientRef = useRef(newRef());

  const late = !!folio && !folio.isOpen && folio.canPostLate;

  useEffect(() => {
    if (!isOpen || !folio) return;
    clientRef.current = newRef();
    setChargeCode(''); setDescription(''); setQuantity('1'); setUnitPrice('');
    setChargeDate(isoDate(folio.businessDate));
    setReference(''); setReferenceDate(''); setAttachment(null); setReason(''); setPaidOut('');
    setPreview(null); setPreviewError(null); setRefusal(null); setIncludesTax(null);
    chargeTypeService.getLookup(true)
      .then((list) => setTypes(list))
      .catch(() => setTypes([]));
  }, [isOpen, folio]);

  // Only the natures the user may post.
  const allowed = useMemo(() => types.filter((t) => {
    const n = natureOf(t);
    if (n === 'RECOVERY') return can(INHOUSE_ACTIONS.postRecovery);
    if (n === 'DISBURSEMENT') return can(INHOUSE_ACTIONS.postDisbursement);
    return true;
  }), [types, can]);
  const options = useMemo(() => allowed.map((t) => ({
    value: t.chargeCode,
    label: natureOf(t) === 'SERVICE' ? t.chargeName : `${t.chargeName} — ${natureOf(t) === 'RECOVERY' ? 'recovery' : 'at cost'}`,
  })), [allowed]);
  const selected = types.find((t) => t.chargeCode === chargeCode) ?? null;
  const nature = natureOf(selected);
  const disbursement = nature === 'DISBURSEMENT';
  const recovery = nature === 'RECOVERY';

  useEffect(() => { setPrintAfter(nature !== 'SERVICE'); }, [nature]);

  const qty = Number(quantity);
  const price = Number(unitPrice);
  const priced = !!chargeCode && qty > 0 && price > 0 && !!chargeDate;
  const missing = recovery && reason.trim().length < 3 ? 'Say what is being recovered and why.'
    : disbursement && !reference.trim() ? "Enter the supplier's bill number."
    : disbursement && !attachment ? "Attach the supplier's bill."
    : disbursement && !paidOut ? 'Say who paid the supplier.'
    : late && !can(INHOUSE_ACTIONS.postLateCharge) ? 'You may not post charges after checkout.'
    : null;
  const valid = priced && !missing;

  const request = useCallback((): PostFolioChargeRequest => ({
    chargeCode,
    description: description.trim() || null,
    quantity: qty,
    unitPrice: price,
    chargeDate,
    amountIncludesTax: disbursement ? false : includesTax,
    referenceNumber: reference.trim() || null,
    referenceDate: referenceDate || null,
    attachmentPath: attachment?.relativePath ?? null,
    attachmentName: attachment?.fileName ?? null,
    reason: reason.trim() || null,
    paidOutMode: disbursement ? paidOut || null : null,
    clientRef: clientRef.current,
  }), [chargeCode, description, qty, price, chargeDate, disbursement, includesTax, reference, referenceDate, attachment, reason, paidOut]);
  const inclusive = !disbursement && (includesTax ?? preview?.amountIncludesTax ?? false);

  // Live preview: net, tax and payer, from the same server calculation the posting uses.
  const previewKey = `${chargeCode}|${qty}|${price}|${chargeDate}|${includesTax}`;
  useEffect(() => {
    if (!isOpen || !folio || !priced) { setPreview(null); setPreviewError(null); return; }
    const handle = window.setTimeout(() => {
      folioService.previewCharge(folio.folioId, request())
        .then((p) => { setPreview(p); setPreviewError(null); })
        .catch((err: any) => { setPreview(null); setPreviewError(err?.response?.data?.message || err?.message || 'Could not price this charge.'); });
    }, 350);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, folio, priced, previewKey]);

  const submit = useCallback(async () => {
    if (!folio || !valid) return;
    // Reserved inside the click, or the browser blocks the voucher's tab.
    const tab = printAfter ? reserveTab() : null;
    setPosting(true);
    setRefusal(null);
    try {
      const result = await folioService.postCharge(folio.folioId, request());
      if (!result.success) {
        tab?.close();
        setRefusal(result.message || 'The folio refused the charge.');
        return;
      }
      const voucher = result.voucherNumber ? ` Voucher ${result.voucherNumber}.` : '';
      notificationService.toast.success(result.wasAlreadyPosted ? `Already posted — nothing charged twice.${voucher}` : `Charge posted.${voucher}`);
      if (tab && result.voucherId) {
        try { showPdf(await folioService.voucherPdf(result.voucherId), tab); } catch { tab.close(); }
      } else {
        tab?.close();
      }
      onPosted();
    } catch (err: any) {
      tab?.close();
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Posting failed.');
    } finally {
      setPosting(false);
    }
  }, [folio, valid, printAfter, request, onPosted]);

  if (!isOpen || !folio) return null;

  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: 10060 }} />
      <div className="modal-container modal-md" style={{ zIndex: 10061 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{late ? 'Late charge' : 'Post charge'} — {folio.folioNumber}</h2>
          <button className="modal-close" onClick={onClose} disabled={posting}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {late && (
            <div style={{ marginBottom: '0.6rem', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.82rem',
              background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}>
              Checked out on {fmtDate(folio.checkedOutOn)}. A late charge (up to {folio.lateChargeDays} days after checkout)
              reopens the folio and is billed on a supplementary invoice.
            </div>
          )}
          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect
                label={<>Charge type <span className="text-danger">*</span></>}
                options={options}
                value={chargeCode}
                onChange={(v) => setChargeCode((v as string) || '')}
                placeholder="Select..."
                menuPortalTarget={document.body}
                helperText={selected && nature !== 'SERVICE'
                  ? CHARGE_NATURE_OPTIONS.find((o) => o.value === nature)?.hint
                  : 'Room and meal-plan nights are posted by night audit.'}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Date <span className="text-danger">*</span></label>
              <input type="date" className="form-input" value={chargeDate}
                min={isoDate(folio.openedOn)} max={isoDate(folio.businessDate)}
                onChange={(e) => setChargeDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity <span className="text-danger">*</span></label>
              <input type="number" className="form-input" value={quantity} min={0.01} step={1}
                onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">
                {disbursement ? 'Amount paid (at cost, no tax)' : `Unit price (${inclusive ? 'including tax' : 'before tax'})`} <span className="text-danger">*</span>
              </label>
              <input type="number" className="form-input" value={unitPrice} min={0.01} step={0.01}
                onChange={(e) => setUnitPrice(e.target.value)} />
              {!disbursement && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginTop: '0.3rem', cursor: 'pointer' }}
                  title="Ticked: the tax is worked out of the amount and the rest is revenue. Defaults from the property's Prices Include Tax setting.">
                  <input type="checkbox" checked={inclusive} onChange={(e) => setIncludesTax(e.target.checked)} />
                  Amount includes tax
                </label>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input type="text" className="form-input" value={description} maxLength={200}
              placeholder={disbursement ? 'e.g. Airport taxi, medicines bought for the guest' : "Shown on the folio — blank uses the charge type's name"}
              onChange={(e) => setDescription(e.target.value)} />
          </div>

          {(recovery || disbursement) && (
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">{disbursement ? 'Supplier bill no.' : 'Reference'} {disbursement && <span className="text-danger">*</span>}</label>
                <input type="text" className="form-input" value={reference} maxLength={60}
                  placeholder={disbursement ? 'As on the supplier’s bill' : 'Damage report, inventory note…'}
                  onChange={(e) => setReference(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">{disbursement ? 'Bill date' : 'Reference date'}</label>
                <input type="date" className="form-input" value={referenceDate} max={isoDate(folio.businessDate)}
                  onChange={(e) => setReferenceDate(e.target.value)} />
              </div>
              {disbursement && (
                <div className="form-group">
                  <SyutSelect
                    label={<>Paid by <span className="text-danger">*</span></>}
                    options={PAID_OUT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    value={paidOut}
                    onChange={(v) => setPaidOut((v as string) || '')}
                    placeholder="Select..."
                    menuPortalTarget={document.body}
                    helperText="Printed on the voucher."
                  />
                </div>
              )}
              <div className="form-group">
                <FileUploadField compact category="folio_voucher" value={attachment} onChange={setAttachment}
                  label={<>{disbursement ? "Supplier's bill" : 'Photo / document'} {disbursement && <span className="text-danger">*</span>}</>}
                  disabled={posting} />
              </div>
            </div>
          )}
          {(recovery || disbursement) && (
            <div className="form-group">
              <label className="form-label">Reason {recovery && <span className="text-danger">*</span>}</label>
              <input type="text" className="form-input" value={reason} maxLength={500}
                placeholder={recovery ? 'What was damaged or lost, and how' : 'Why it was bought for the guest (optional)'}
                onChange={(e) => setReason(e.target.value)} />
            </div>
          )}

          {previewError && <div className="form-error-message" style={{ marginTop: '0.25rem' }}>{previewError}</div>}
          {preview && (
            <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.8rem', borderRadius: '6px', background: 'var(--bg-secondary)', fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{disbursement ? 'At cost' : 'Net'}</span><span>{formatCurrency(preview.net)}</span></div>
              {disbursement
                ? <div style={{ color: 'var(--text-muted)' }}>No tax — billed as a reimbursement, outside the invoice's taxable value.</div>
                : preview.taxCalculated
                  ? preview.components.map((c) => (
                    <div key={c.componentCode} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)' }}>
                      <span>{c.componentName || c.componentCode} {c.ratePercentage}%</span><span>{formatCurrency(c.taxAmount)}</span>
                    </div>))
                  : <div style={{ color: 'var(--text-muted)' }}>Tax calculation is off for this property — posted without tax.</div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginTop: '0.25rem' }}>
                <span>Total</span><span>{formatCurrency(preview.total)}</span>
              </div>
              <div style={{ marginTop: '0.35rem', color: 'var(--text-secondary)' }}>
                Billed to <b>{preview.payerType === 'GUEST' ? 'the guest' : preview.payerName}</b>
                {preview.payerType !== 'GUEST' && ' (settlement)'}
                {preview.placeOfSupplyCode && <> · place of supply {preview.placeOfSupplyCode}</>}
              </div>
            </div>
          )}
          {priced && missing && <div className="form-error-message" style={{ marginTop: '0.4rem' }}>{missing}</div>}
          {refusal && (
            <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem',
              background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}>
              {refusal}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginRight: 'auto', cursor: 'pointer' }}
            title="Opens the numbered voucher for the guest to sign">
            <input type="checkbox" checked={printAfter} onChange={(e) => setPrintAfter(e.target.checked)} /> Print the voucher
          </label>
          <button className="btn btn-secondary" onClick={onClose} disabled={posting}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={posting || !valid || !!previewError}>
            {posting ? 'Posting…' : late ? 'Post late charge' : 'Post charge'}
          </button>
        </div>
      </div>
    </>
  );
};

export default PostChargeModal;
